import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {secondsToMinutes} from "./helpers/secondsToMinutes";
import { getApiErrorMessage } from "./helpers/apiErrorMessage";
import AlbumSearchSkeleton from "./AlbumSearchSkeleton";
import Pagination from "./Pagination";

function Diary() {
    const [query, setQuery] = useState('');
    const [album, setAlbum] = useState(null);
    const [searchResults, setSearchResults] = useState([]);
    const [searchCache, setSearchCache] = useState({});
    const [currentResultIndex, setCurrentResultIndex] = useState(0);
    const [searching, setSearching] = useState(false);
    const [searchStage, setSearchStage] = useState('');
    const [searchProgress, setSearchProgress] = useState({});
    const [searchError, setSearchError] = useState(false);
    const [diary, setDiary] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [logDate, setLogDate] = useState('');
    const [expandedDetails, setExpandedDetails] = useState(null);
    const [editing, setEditing] = useState(null);
    const [diarySettings, setDiarySettings] = useState({ enableRatings: true, showCoverArt: true });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const searchSessionRef = useRef(0);
    useEffect(() => { fetchDiary(1); fetchDiarySettings(); }, []);

    const fetchDiary = async (pageNumber = page) => {
        try {
            setError(null);
            const response = await axios.get(`/api/diary/?page=${pageNumber}`);
            setDiary(response.data.entries);
            setPage(response.data.page);
            setTotalPages(response.data.totalPages);
        } catch (error) {
            setError(getApiErrorMessage(error, 'load diary entries'));
            console.error('Error fetching diary:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage < 1 || newPage > totalPages || newPage === page) return;
        fetchDiary(newPage);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') { searchAlbum(); }
    };

    const searchAlbum = async () => {
        if (!query.trim() || searching) return;
        const sessionId = searchSessionRef.current + 1;
        searchSessionRef.current = sessionId;

        setAlbum(null);
        setSearching(true);
        setSearchStage('Finding best matches...');
        setSearchError(false);
        try {
            const response = await axios.get(`/api/search?query=${encodeURIComponent(query)}&mode=basic`);
            const results = Array.isArray(response.data)
                ? response.data
                : response.data?.results || (response.data ? [response.data] : []);

            if (!results.length) {
                setAlbum(null);
                setSearchResults([]);
                setCurrentResultIndex(0);
                setSearchError(true);
                return;
            }

            setSearchResults(results);
            const initialCache = {};
            const initialProgress = {};
            for (const result of results) {
                initialCache[result.mbid] = result;
                initialProgress[result.mbid] = {
                    isLoading: true,
                    label: 'Queued for staged loading...',
                    contextLoaded: false,
                    releaseLoaded: false,
                    artistLoaded: false,
                    coverLoaded: false,
                    hasError: false
                };
            }
            setSearchCache(initialCache);
            setSearchProgress(initialProgress);
            setCurrentResultIndex(0);
            setAlbum(results[0]);

            for (const result of results) {
                preloadAlbumStages(result, initialCache, sessionId);
            }
        } catch (error) {
            console.error("Album not found");
            setSearchError(true);
            setSearchStage('Search failed');
        } finally {
            setSearching(false);
        }
    };

    const mergeAlbumData = (mbid, fallbackAlbum, patch) => {
        /** Merges new album data into the existing cache, search results, and current album state */
        setSearchCache(prevCache => {
            const merged = { ...(prevCache[mbid] || fallbackAlbum || {}), ...patch };
            return { ...prevCache, [mbid]: merged };
        });

        setSearchResults(prevResults => prevResults.map(result =>
            result.mbid === mbid ? { ...result, ...patch } : result
        ));

        setAlbum(prevAlbum => prevAlbum?.mbid === mbid ? { ...prevAlbum, ...patch } : prevAlbum);
    };

    const updateProgress = (mbid, patch) => {
        /** Updates the progress state for a specific album MBID */
        setSearchProgress(prevProgress => ({
            ...prevProgress,
            [mbid]: {
                ...(prevProgress[mbid] || {}),
                ...patch
            }
        }));
    };

    const preloadAlbumStages = async (basicAlbum, initialCache, sessionId) => {
        const mbid = basicAlbum?.mbid;
        if (!mbid) return;

        updateProgress(mbid, { label: 'Loading release-group context...', isLoading: true });

        let releaseId = null;
        let artistId = null;

        try {
            const contextResponse = await axios.get(`/api/search/stage/context/${encodeURIComponent(mbid)}`);
            if (searchSessionRef.current !== sessionId) return;

            releaseId = contextResponse.data?.releaseId;
            artistId = contextResponse.data?.artistId;
            const tags = contextResponse.data?.tags || [];

            mergeAlbumData(mbid, initialCache[mbid], { tags });
            updateProgress(mbid, { contextLoaded: true, label: 'Loading release data...' });
        } catch (error) {
            if (searchSessionRef.current !== sessionId) return;
            updateProgress(mbid, {
                hasError: true,
                isLoading: false,
                label: 'Could not load release-group context'
            });
            return;
        }

        if (releaseId) {
            try {
                const releaseResponse = await axios.get(`/api/search/stage/release/${encodeURIComponent(releaseId)}`);
                if (searchSessionRef.current !== sessionId) return;

                const country = releaseResponse.data?.country || null;
                const duration = releaseResponse.data?.duration || 0;
                mergeAlbumData(mbid, initialCache[mbid], { country, duration });
                updateProgress(mbid, { releaseLoaded: true, label: 'Loading artist data...' });
            } catch (error) {
                if (searchSessionRef.current !== sessionId) return;
                updateProgress(mbid, {
                    hasError: true,
                    isLoading: false,
                    label: 'Could not load release data'
                });
                return;
            }
        }

        if (artistId) {
            try {
                const artistResponse = await axios.get(`/api/search/stage/artist/${encodeURIComponent(artistId)}`);
                if (searchSessionRef.current !== sessionId) return;

                const artistCountry = artistResponse.data?.country;
                if (artistCountry) {
                    mergeAlbumData(mbid, initialCache[mbid], { country: artistCountry });
                }
                updateProgress(mbid, { artistLoaded: true, label: 'Loading cover art...' });
            } catch (error) {
                if (searchSessionRef.current !== sessionId) return;
                updateProgress(mbid, {
                    hasError: true,
                    isLoading: false,
                    label: 'Could not load artist data'
                });
                return;
            }
        }

        try {
            const coverResponse = await axios.get(`/api/search/stage/cover/${encodeURIComponent(mbid)}`);
            if (searchSessionRef.current !== sessionId) return;

            mergeAlbumData(mbid, initialCache[mbid], {
                albumCover: coverResponse.data?.albumCover || null,
                detailsLoaded: true
            });
            updateProgress(mbid, {
                coverLoaded: true,
                isLoading: false,
                label: 'Album found'
            });
        } catch (error) {
            if (searchSessionRef.current !== sessionId) return;
            updateProgress(mbid, {
                hasError: true,
                isLoading: false,
                label: 'Could not load cover art'
            });
        }
    };

    const fetchDiarySettings = async () => {
        try {
            const response = await axios.get('/api/settings/diary');
            setDiarySettings(response.data);
        } catch (error) {
            setError(getApiErrorMessage(error, 'load diary settings'));
            console.error('Error fetching diary settings:', error);
        }
    };

    const logAlbum = async () => {
        try {
            setError(null);
            const wishlistResponse = await axios.get('/api/list/wishlist');  // Remove from wishlist if present
            const wishlist = wishlistResponse.data;
            const albumInWishlist = wishlist.find(item => item.album.mbid === album.mbid);
            if (albumInWishlist) {
                await axios.delete(`/api/list/wishlist/${albumInWishlist.listEntryId}`);
            }

            await axios.post('/api/diary/add', {
                ...album,
                logDate: logDate || undefined  // send the chosen log date or leave it blank for today
            });
            await cancelSearch();
            await fetchDiary(1);  // new entries sort to the top, so jump back to page 1
        } catch (error) {
            setError(getApiErrorMessage(error, 'log this album'));
            console.error("Error logging album: ", error.message);
        }
    };

    const handleLikeClick = async (entryId, currentLikeStatus) => {
        setDiary(currentDiary => currentDiary.map(entry =>
            entry.entryId === entryId
                ? { ...entry, isLiked: !currentLikeStatus }
                : entry
        ));

        try {
            setError(null);
            await axios.post(`/api/diary/like/${entryId}`);
        } catch (error) {
            setError(getApiErrorMessage(error, 'update like status'));
            console.error("Error liking album: ", error.message);
        }
    };

    const handleRating = async (entryId, star) => {
        const currentEntry = diary.find(entry => entry.entryId === entryId);
        if (!currentEntry) return;

        const currentRating = currentEntry.rating;
        let newRating = star;

        // If clicking on the first star and it's already rated 1, set to 0 (delete rating)
        if (star === 1 && currentRating === 1) {
            newRating = 0;
        }

        setDiary(currentDiary => currentDiary.map(entry =>
            entry.entryId === entryId
                ? { ...entry, rating: newRating }
                : entry
        ));

        try {
            setError(null);
            await axios.post(`/api/diary/rate`, {
                entryId: entryId,
                ratingValue: newRating
            });
        } catch (error) {
            setError(getApiErrorMessage(error, 'update rating'));
            console.error("Error rating album: ", error.message);
        }
    };

    const toggleEditing = (entryId) => {
        setExpandedDetails(null);
        setEditing(editing === entryId ? null : entryId);
    };

    const toggleDetails = (entryId) => {
        setEditing(null);
        setExpandedDetails(expandedDetails === entryId ? null : entryId);
    };

    const handleInputChange = async (entryId) => {
        setEditing(null);
        const newDate = document.getElementById(`log-date${entryId}`).value;
        const newContent = document.getElementById(`content${entryId}`).value;

        setDiary(currentDiary => currentDiary.map(entry =>
            entry.entryId === entryId
                ? { ...entry, addedOn: newDate, content: newContent }
                : entry
        ));

        try {
            setError(null);
            await axios.post(`/api/diary/change`, {
                entryId: entryId,
                newDate: newDate,
                content: newContent
            });
            await fetchDiary(page);  // the date change can shift entry ordering across pages
        } catch (error) {
            setError(getApiErrorMessage(error, 'update this diary entry'));
            console.error("Error changing diary entry: ", error.message);
        }
    };

    const handleDelete = async (entryId) => {
        try {
            setError(null);
            await axios.delete(`/api/diary/${entryId}`);
            setEditing(null)
            setExpandedDetails(null);
            const isLastEntryOnPage = diary.length === 1 && page > 1;
            await fetchDiary(isLastEntryOnPage ? page - 1 : page);
        } catch (error) {
            setError(getApiErrorMessage(error, 'delete this diary entry'));
            console.error("Error deleting album: ", error.message);
        }
    };

    const cancelSearch = async () => {
        searchSessionRef.current += 1;
        setAlbum(null);
        setSearchResults([]);
        setSearchCache({});
        setSearchProgress({});
        setCurrentResultIndex(0);
        setSearchStage('');
    };

    const goToPreviousSearchResult = () => {
        if (currentResultIndex <= 0) return;
        const nextIndex = currentResultIndex - 1;
        setCurrentResultIndex(nextIndex);
        const nextAlbum = searchResults[nextIndex];
        setAlbum(searchCache[nextAlbum.mbid] || nextAlbum);
    };

    const goToNextSearchResult = () => {
        if (currentResultIndex >= searchResults.length - 1) return;
        const nextIndex = currentResultIndex + 1;
        setCurrentResultIndex(nextIndex);
        const nextAlbum = searchResults[nextIndex];
        setAlbum(searchCache[nextAlbum.mbid] || nextAlbum);
    };

    const currentSearchResult = searchResults[currentResultIndex] || null;
    const currentMbid = currentSearchResult?.mbid;
    const currentAlbum = currentMbid ? (searchCache[currentMbid] || currentSearchResult) : album;
    const currentProgress = currentMbid ? searchProgress[currentMbid] : null;
    const showSkeleton = searching || Boolean(currentProgress?.isLoading);

    if (loading) {
        return <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">Loading...</div>;
    }

    return (
        <div className="bg-gray-900 text-white font-sans min-h-screen">
            {error && (
                <div className="bg-red-900 border-l-4 border-red-600 p-4 m-4 rounded">
                    <div className="flex justify-between items-center">
                        <p className="text-red-200">{error}</p>
                        <button
                            onClick={() => setError(null)}
                            className="text-red-400 hover:text-red-200 text-2xl"
                        >
                            x
                        </button>
                    </div>
                </div>
            )}
            <div className="flex flex-col items-center w-full">
                <div className="flex items-center w-full max-w-lg">
                    <input
                        type="text"
                        placeholder="Search for an album or use 'mbid:'"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="flex-grow bg-gray-700 text-gray-300 p-2 mb-5 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <button
                        onClick={searchAlbum}
                        disabled={searching}
                        className="bg-green-500 text-white p-2 rounded-r-lg hover:bg-green-600 transition-colors duration-200 mb-5"
                    >
                        {searching ? (
                            <span className="flex items-center gap-2">
                                <i className="fas fa-spinner animate-spin"></i>
                                Searching
                            </span>
                        ) : 'Search'}
                    </button>
                </div>

                {(currentAlbum || showSkeleton) && (
                    <AlbumSearchSkeleton
                        label={showSkeleton ? (searching ? (searchStage || 'Querying MusicBrainz...') : (currentProgress?.label || 'Loading album details...')) : 'Album found'}
                        album={currentAlbum}
                        loading={showSkeleton}
                        showBack={currentResultIndex > 0}
                        showNext={currentResultIndex < searchResults.length - 1}
                        onBack={goToPreviousSearchResult}
                        onNext={goToNextSearchResult}
                    />
                )}

                {searchError && (
                    <div className="bg-red-900 border-l-4 border-red-600 p-4 m-4 rounded w-full max-w-lg">
                        <p className="text-red-200">Album not found. Please try a different search.</p>
                        <button 
                            onClick={() => setSearchError(false)}
                            className="text-red-400 hover:text-red-200 text-2xl"
                        >
                            x
                        </button>
                    </div>
                )}

                {currentAlbum && !showSkeleton && (
                    <div key={currentAlbum.mbid}>
                        <div className="flex items-center space-x-4">
                            <input
                                type="date"
                                value={logDate}
                                onChange={(e) => setLogDate(e.target.value)}
                                placeholder="YYYY-MM-DD"
                                className="mb-5 bg-gray-700 text-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                            <button
                                className="bg-green-500 text-white p-2 mb-5 rounded-lg hover:bg-green-600 transition-colors duration-200"
                                onClick={logAlbum}>Log Album</button>
                            <button className="bg-gray-500 text-white p-2 mb-5 rounded-lg hover:bg-gray-600 transition-colors duration-200"
                                    onClick={cancelSearch}>Cancel</button>
                        </div>
                    </div>
            )}

            </div>

            <div className="mt-6 ml-40 mr-40">
                {diary.map((entry, index) => {
                    const [year, month, day] = entry.addedOn.split('-');
                    const monthNames = [
                        "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
                        "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"
                    ];
                    const monthName = monthNames[parseInt(month, 10) - 1];

                    const nextEntry = diary[index - 1];
                    const isLastOfMonth = !nextEntry || nextEntry.addedOn.slice(0, 7) !== entry.addedOn.slice(0, 7);

                    return (
                        <div key={entry.entryId} className="flex flex-col hover:bg-gray-800 transition-colors duration-200">
                            <div className="flex items-center border-t border-gray-700 py-4 px-4">
                                {isLastOfMonth ? (
                                    <div className="flex">
                                        <div
                                            className="flex items-center flex-col justify-center w-12 h-12 bg-gray-700 ml-2 rounded">
                                            <span className="text-sm font-semibold">{monthName}</span>
                                            <span className="text-sm font-semibold">{year}</span>
                                        </div>
                                        <div className="flex items-center text-sm justify-center ml-4 mr-2 text-gray-500"
                                             style={{fontSize: '1.25rem'}}>{day}</div>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center ml-16 p-2 text-gray-500"
                                         style={{fontSize: '1.25rem'}}>{day}</div>
                                )}
                                <div className="ml-3 flex-grow flex items-center">
                                    {diarySettings.showCoverArt && (
                                        <img
                                            src={entry.album.albumCover || 'https://placehold.co/75x75'}
                                            alt={entry.album.title}
                                            className="inline-block mr-4 w-12 h-12 rounded-lg"
                                        />
                                    )}
                                    <div className="flex flex-col">
                                        <div className="flex items-center">
                                            <span className="font-bold text-lg">{entry.album.title}</span>
                                            <p className="text-gray-400 text-sm ml-1.5 mt-[3px]">{entry.album.releaseDate}</p>
                                        </div>
                                        <p className="text-gray-400 text-sm">{entry.album.artist}</p>
                                    </div>
                                </div>
                                <div className="flex flex-center items-center justify-center mr-6">
                                    {diarySettings.enableRatings && (
                                        [1, 2, 3, 4, 5].map((star) => (
                                            <i
                                                key={star}
                                                className={`fas fa-star ${
                                                    star <= entry.rating ? 'text-yellow-400' : 'text-gray-500'
                                                } mb-[1px] cursor-pointer`}
                                                onClick={() => handleRating(entry.entryId, star)}
                                            ></i>
                                        ))
                                    )}
                                    <i
                                        className={`fas fa-heart ${entry.isLiked ? 'text-orange-500' : 'text-gray-500'} ml-2 mr-2 cursor-pointer hover:text-orange-500`}
                                        onClick={() => handleLikeClick(entry.entryId, entry.isLiked)}
                                    ></i>
                                    <i
                                        className={`fas fa-pencil-alt text-gray-${editing === entry.entryId ? '300' : '500'} mr-2 cursor-pointer hover:text-gray-300`}
                                        onClick={() => toggleEditing(entry.entryId)}
                                    ></i>
                                    <i
                                        className={`fas fa-book text-gray-${expandedDetails === entry.entryId ? '300' : '500'} mr-2 cursor-pointer hover:text-gray-300`}
                                        onClick={() => toggleDetails(entry.entryId)}
                                    ></i>
                                </div>
                            </div>
                            <div
                                // id={`details${index}`}
                                // className="overflow-hidden transition-all duration-1000 ease-in-out h-0"
                                className={`overflow-hidden transition-all duration-1000 ease-in-out ${
                                    expandedDetails === entry.entryId ? 'h-96' : 'h-0'
                                }`}
                            >
                                {expandedDetails === entry.entryId && (
                                    <div className="flex items-start space-x-20">
                                        <div className="ml-28 mb-4 text-gray-300 space-y-1">
                                            <div className="grid grid-cols-2 w-96 space-y-1">
                                                <p><strong>Artist:</strong> {entry.album.artist}</p>
                                                <p><strong>Title:</strong> {entry.album.title}</p>
                                                <p><strong>Country:</strong> {entry.album.country}</p>
                                                <p><strong>Year:</strong> {entry.album.releaseDate}</p>
                                            </div>
                                            {entry.content && (<p><strong>Content:</strong> {entry.content || ''}</p>)}
                                            <p><strong>MBID:</strong> {entry.album.mbid}</p>
                                            <p><strong>Duration:</strong> {secondsToMinutes(entry.album.duration)}</p>
                                            <p><strong>Added on:</strong> {entry.addedOn}</p>
                                            <p><strong>Tags:</strong></p><p className="ml-10 w-[500px]">{entry.album.tags.join(', ')}</p>
                                        </div>
                                        {diarySettings.showCoverArt && (
                                            <img
                                                src={entry.album.albumCover || 'https://placehold.co/75x75'}
                                                alt={entry.album.title}
                                                className="inline-block w-[370px] justify-right"
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                            <div
                                // id={`editing${index}`}
                                // className="overflow-hidden transition-all duration-1000 ease-in-out h-0"
                                className={`overflow-hidden transition-all duration-1000 ease-in-out ${
                                    editing === entry.entryId ? 'h-48' : 'h-0'
                                }`}
                            >
                                {editing === entry.entryId && (
                                    <div className="flex items-start space-x-20 p-4">
                                        <div
                                            className="flex flex-col ml-28 p-4 gap-4 rounded-lg shadow-lg w-full max-w-lg">
                                            <div className="flex items-start gap-2">
                                                <label htmlFor="log-date" className="text-white py-1 w-24">Log date:</label>
                                                <input
                                                    type="date"
                                                    id={`log-date${entry.entryId}`}
                                                    defaultValue={entry.addedOn}
                                                    className="flex-1 px-2 py-1 bg-gray-800 text-gray-300 rounded-md focus:ring focus:ring-indigo-200"
                                                />
                                            </div>
                                            <div className="flex items-start gap-2">
                                                <label htmlFor="content" className="text-white py-1 w-24">Content:</label>
                                                <input
                                                    type="text"
                                                    id={`content${entry.entryId}`}
                                                    defaultValue={entry.content || ''}
                                                    className="flex-1 px-2 py-1 bg-gray-800 text-gray-300 rounded-md focus:ring focus:ring-indigo-200"
                                                    placeholder="Write a note..."
                                                />
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleInputChange(entry.entryId)}
                                            className="ml-2 mt-3 p-2 text-gray-500 bg-transparent rounded hover:bg-green-100 hover:text-green-600"
                                        >
                                            <i className="fas fa-check"/>
                                        </button>
                                        <button
                                            onClick={() => handleDelete(entry.entryId)}
                                            className="ml-2 mt-3 p-2 text-gray-500 bg-transparent rounded hover:bg-red-100 hover:text-red-600"
                                        >
                                            <i className="fas fa-trash-alt"/>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <Pagination currentPage={page} totalPages={totalPages} onPageChange={handlePageChange} />
        </div>
    )
}

export default Diary;